import React, { useState, useContext, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { SummaryPlayer } from './ui/SummaryPlayer/SummaryPlayer'
import { SummaryNotes } from './ui/SummaryNotes/SummaryNotes'
import { LogoutButton } from 'components/LogoutButton'
import { ReactComponent as Logo } from 'i/logo.svg'
import styles from './SummaryPage.module.scss'
import { SummaryTranscription } from './ui/SummaryTranscription/SummaryTranscription'
import { useParams } from 'react-router-dom'
import { callApi } from 'api'
import { postCallSummary } from 'api/call'
import { POPUPS_ID, PROMISE_STATES } from 'utils/constants'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { connect } from 'react-redux'
import getCallType from 'utils/getCallType'
import calculateFormattedDateTime from 'utils/calculateFormattedDateTime'
import { getCustomSummaryTitle, setCustomSummaryTitle } from 'utils/customSummaryTitle'
import useFetchWithRetry from 'hooks/useFetchWithRetry'
import SummaryPopup from 'components/Popups/SummaryPopup/SummaryPopup'
import { ReactComponent as BackIcon } from 'i/icons/triangle-left.svg'
import { ReactComponent as PenIcon } from 'i/icons/pen.svg'
import ContentEditable from 'react-contenteditable'

import { UiContext } from 'context/ui'
import { Button } from 'components/ui/Button'
import { LoadingBarBlock } from 'components/LoadingBarBlock'

const TABS = {
  nav: [
    {
      tabName: 'notes',
      title: 'Meeting notes',
    },
    {
      tabName: 'transcript',
      title: 'Transcript',
    },
    {
      tabName: 'video',
      title: 'Video',
    },
  ],
}

const SummaryPage = (props) => {
  const [activeTab] = useState(TABS.nav[0].tabName)
  const [sendingMessageStatus, setSendingMessageStatus] = useState(PROMISE_STATES.idle)
  const { id } = useParams()
  const callType = getCallType()
  const [isEditMode, setEditMode] = useState(false)
  const [isTitleEditMode, setTitleEditMode] = useState(false)
  const [isTitleSaving, setTitleSaving] = useState(false)
  const [updatedTitle, setUpdatedTitle] = useState('')
  const titleEditableRef = useRef(null)

  const { loading, error, data } = useFetchWithRetry(() =>
    callApi.getCallSummary(callType, id)
  )

  const { activePopupId, setActivePopupId } = useContext(UiContext)

  const history = useHistory()

  useEffect(() => {
    const serverTitle = data?.result?.data?.title || ''
    const customTitle = getCustomSummaryTitle(callType, id)
    setUpdatedTitle(customTitle || serverTitle)
  }, [callType, data?.result?.data?.title, id])

  if (loading === PROMISE_STATES.rejected && error) {
    if (props.isPro) {
      history.push('/dashboard/pro')
    } else {
      history.push('/dashboard/user')
    }
  }

  const shareHandler = async () => {
    if (data.result.data.instant_call) {
      setActivePopupId(POPUPS_ID.summary)
    } else {
      setSendingMessageStatus(PROMISE_STATES.pending)
      await callApi.shareMessage(callType, id).then((res) => {
        if (res.error) {
          setSendingMessageStatus(PROMISE_STATES.idle)
        } else {
          setSendingMessageStatus(PROMISE_STATES.fulfilled)
          setTimeout(() => {
            setSendingMessageStatus(PROMISE_STATES.idle)
          }, 1000)
        }
      })
    }
  }

  const localTimeResult = calculateFormattedDateTime(
    data?.result.data.scheduledAt,
    'Etc/Greenwich',
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  const handleTitleChange = ({ target }) => {
    setUpdatedTitle(target.value)
  }

  const enterTitleEditMode = () => {
    setTitleEditMode(true)
    titleEditableRef?.current?.el?.current?.focus()
  }

  const saveUpdatedTitle = () => {
    const persistTitle = async () => {
      if (isTitleSaving) return

      setTitleSaving(true)
      const existingNotes = data?.result?.data?.meetingNotes || ''
      const nextTitle = updatedTitle.replace(/<[^>]*>/g, '').trim()
      setCustomSummaryTitle(callType, id, nextTitle)

      try {
        const summaryRes = await postCallSummary(callType, id, {
          title: nextTitle,
          meeting_notes: existingNotes,
        })
        if (summaryRes?.error) {
          throw new Error(summaryRes.error.message || 'Failed to edit summary title')
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save title:', err)
      } finally {
        setTitleSaving(false)
        setTitleEditMode(false)
      }
    }

    persistTitle()
  }

  const toggleTitleMode = () => {
    if (isTitleEditMode) {
      saveUpdatedTitle()
    } else {
      enterTitleEditMode()
    }
  }

  return (
    <section className={classNames('section', styles.section)}>
      <SummaryPopup
        isOpen={activePopupId === POPUPS_ID.summary}
        callId={props.match.params.id}
      />
      <a
        href="/dashboard/pro"
        className={classNames(styles.back, {
          [styles.backV2]: props.isPro,
        })}
      >
        <div className={styles.backIcon}>
          <BackIcon />
        </div>
        {'Back'}
      </a>
      {loading === PROMISE_STATES.pending && !data?.result?.data?.meetingNotes ? (
        <LoadingBarBlock title="Meeting notes are being generated" />
      ) : null}
      {loading === PROMISE_STATES.fulfilled && data?.result?.data?.meetingNotes ? (
        <>
          {!props.isPro && <LogoutButton className={styles.logout} text="Logout" />}
          <div className={styles.head}>
            <div className={styles.logo}>
              <Logo />
            </div>
            <div className={styles.titleWrap}>
              <ContentEditable
                ref={titleEditableRef}
                className={classNames(styles.title, {
                  [styles.titleEditable]: props.isPro,
                  [styles.titleEditMode]: isTitleEditMode,
                })}
                html={updatedTitle}
                disabled={!isTitleEditMode}
                onChange={handleTitleChange}
              />
              {props.isPro && (
                <button
                  type="button"
                  aria-label="Change meeting title"
                  className={classNames(styles.titleTrigger, {
                    [styles.titleTriggerSave]: isTitleEditMode,
                  })}
                  disabled={isTitleSaving}
                  onClick={toggleTitleMode}
                >
                  {isTitleEditMode ? (
                    <div className={styles.titleSaveLabel}>Save</div>
                  ) : (
                    <div className={styles.titleIcon}>
                      <PenIcon />
                    </div>
                  )}
                </button>
              )}
            </div>
            {data?.result?.data?.scheduledAt && (
              <div className={styles.date}>{localTimeResult}</div>
            )}
            {props.isPro && (
              <>
                <Button
                  onClick={shareHandler}
                  sendingMessageStatus={sendingMessageStatus}
                  variant="quinary"
                  successVariant="quaternary"
                  size="md2"
                >
                  Share with attendees
                </Button>
              </>
            )}
          </div>

          <div className={styles.tabs}>
            {/* <div className={styles.nav}>
              {TABS.nav.map(({ title, tabName }) => (
                <button
                  key={tabName}
                  type="button"
                  className={classNames(styles.button, {
                    [styles.active]: tabName === activeTab,
                  })}
                  onClick={() => handleTabClick(tabName)}
                >
                  {title}
                </button>
              ))}
            </div> */}
            <div
              className={classNames(styles.body, {
                [styles.editMode]: isEditMode,
              })}
            >
              {activeTab === 'notes' && (
                <SummaryNotes
                  notes={data?.result?.data?.meetingNotes}
                  title={data?.result?.data?.title}
                  isEditable={props.isPro}
                  isEditMode={isEditMode}
                  callType={callType}
                  callId={id}
                  onEnterEditMode={() => {
                    setEditMode(true)
                  }}
                  onExitEditMode={() => {
                    setEditMode(false)
                  }}
                />
              )}
              {activeTab === 'transcript' && <SummaryTranscription />}
              {activeTab === 'video' && <SummaryPlayer roomID={id} />}
            </div>
          </div>
        </>
      ) : null}
    </section>
  )
}

const mapStateToProps = ({ auth: { error, isPro, user } }) => ({
  authError: error,
  user,
  isPro,
})

export default connect(mapStateToProps)(SummaryPage)
